import { Component, type ErrorInfo, type ReactNode } from "react";
import { Alert, Button } from "../design/ui";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

// Minified React errors only ship an error code; decode it to the real message.
function decodeHint(message: string): string | null {
  const m = message.match(/Minified React error #(\d+)/);
  if (!m) return null;
  if (m[1] === "130") {
    return "React #130 — \"Element type is invalid\": a component rendered as undefined/object. " +
      "Almost always a bad import/export (default vs named) or a package version mismatch in the " +
      "component named at the TOP of the stack below.";
  }
  return `React error #${m[1]} — see https://react.dev/errors/${m[1]}`;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    const hint = decodeHint(error.message);

    return (
      <div style={{ padding: 32, maxWidth: 820, margin: "64px auto 0" }}>
        <Alert tone="danger">
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Something went wrong</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.92em", whiteSpace: "pre-wrap" }}>
            {error.message}
          </div>
          {hint && <div style={{ marginTop: 8 }}>{hint}</div>}
          {error.message.includes("Minified React error") && (
            <div style={{ marginTop: 8, fontSize: 12, color: "var(--kt-text-tertiary)" }}>
              Full message:{" "}
              <a
                href="https://react.dev/errors/130"
                target="_blank"
                rel="noreferrer"
                style={{ color: "var(--kt-teal-ink)" }}
              >
                react.dev/errors
              </a>
              {" "}· for readable errors run <code>npm run dev</code> locally.
            </div>
          )}
        </Alert>

        {componentStack && (
          <div style={{ marginTop: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 500, lineHeight: 1.4, marginBottom: 8, color: "var(--kt-ink)" }}>
              Component stack (with source maps, this names the real file):
            </div>
            <div
              className="kt-card"
              style={{ fontFamily: "var(--font-mono)", fontSize: 12, overflow: "auto", maxHeight: 320, whiteSpace: "pre-wrap" }}
            >
              <pre style={{ margin: 0 }}>{componentStack.trim()}</pre>
            </div>
          </div>
        )}

        <Button variant="primary" onClick={() => window.location.reload()}>
          Reload
        </Button>
      </div>
    );
  }
}
